import { useQuery } from '@tanstack/react-query';
import { getFamiliesWithStats, getAllUsers } from '../services/adminService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './AnalyticsScreen.css';

const COLORS = ['#2AB7A9', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

export default function AnalyticsScreen() {
  const { data: families } = useQuery({
    queryKey: ['families'],
    queryFn: getFamiliesWithStats,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: getAllUsers,
  });

  const subscriptionData = families?.reduce((acc, family) => {
    const tier = family.subscriptionTier || 'none';
    acc[tier] = (acc[tier] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const roleData = [
    { name: 'Caregivers', value: users?.filter(u => u.role === 'user').length || 0 },
    { name: 'Family Members', value: users?.filter(u => u.role === 'familyMember').length || 0 },
    { name: 'Admins', value: users?.filter(u => u.role === 'admin').length || 0 },
  ];

  const familySizeData = families?.map(family => ({
    name: family.name,
    users: family.userCount,
    caregivers: family.caregiverCount,
    members: family.memberCount,
  })) || [];

  return (
    <div className="analytics-screen">
      <div className="screen-header">
        <div>
          <h1>Analytics</h1>
          <p>System-wide statistics and insights</p>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-card">
          <h2>Subscription Tiers</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={Object.entries(subscriptionData).map(([name, value]) => ({ name, value }))}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {Object.entries(subscriptionData).map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-card">
          <h2>User Roles Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={roleData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {roleData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-card full-width">
          <h2>Family Sizes</h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={familySizeData.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="users" fill="#2AB7A9" name="Total Users" />
              <Bar dataKey="caregivers" fill="#3B82F6" name="Caregivers" />
              <Bar dataKey="members" fill="#10B981" name="Members" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

